import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import LoginOutlined from '@ant-design/icons/lib/icons/LoginOutlined';
import LogoutOutlined from '@ant-design/icons/lib/icons/LogoutOutlined';
import MailOutlined from '@ant-design/icons/lib/icons/MailOutlined';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpCol } from 'new-components/DLS/OpCol/OpCol';
import { OpRow } from 'new-components/DLS/OpRow/OpRow';
import { useSelectorJs } from 'utils/customHooks';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';
import { RequestLicensesInlineMessage } from 'new-components/RequestLicenses/RequestLicensesInlineMessage';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useUrlSearchParams } from 'utils/customHooks/useUrlSearchParams';
import { useRemoveQueryParams } from 'utils/customHooks/useRemoveQueryParams';

import { visitorSmallPackageId } from '../../../../constants/packageIds';
import { DATE_TIME_FORMAT } from '../../../../constants/dates';
import {
  fetchInvitationConfig,
  VisitorInvitationState,
} from '../../../../store/slices/visitorInvitationSlice';
import {
  fetchLocations,
  LocationsState,
} from '../../../../store/slices/locationsSlice';
import { setSelectedVisit } from '../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { VisitorSearch } from '../../../../types/visitorSearchTypes';
import { LocationWorkflowResponse } from '../../../../types/visitorsDrawerTypes';
import { VisitorWorkflowConfig } from '../../../../types/visitorWorkflowTypes';
import InvitationDrawer from '../ActionFloatButtons/invite/InvitationDrawer';
import ScanIDModal from '../ActionFloatButtons/signIn/ScanIDModal';
import SignInMethodModal from '../ActionFloatButtons/signIn/SignInMethodModal';
import SignInModal from '../ActionFloatButtons/signIn/SignInModal';
import SignOutModal from '../ActionFloatButtons/signOut/SignOutModal';
import ScanQRCodeModal from '../ActionFloatButtons/signIn/ScanQRCodeModal';
import SignInManuallyDrawer from '../ActionFloatButtons/signIn/SignInManuallyDrawer';
import SelectLocationModal from '../ActionFloatButtons/SelectLocationModal';
import VisitorsDrawer from '../../../../pages/visitors/drawer/VisitorsDrawer';
import { useVisitorFeature } from '../../../../hooks/useVisitorFeature';

import QuickActionCard from './QuickActionCard';

dayjs.extend(utc);
dayjs.extend(timezone);

type QuickAction = 'SIGN_IN' | 'SIGN_OUT' | 'NEW_INVITATION';

interface QuickActionsModalProps {
  open: boolean;
  onClose: () => void;
}

interface SignInWorkflowConfigResponse {
  data?: VisitorWorkflowConfig[] | null;
}

interface SearchVisitorsResponse {
  data?: VisitorSearch[] | null;
}

const QuickActionsModal = ({ open, onClose }: QuickActionsModalProps) => {
  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();

  const { isValid: hasInviteWrite } = useValidateScopes([
    'o{orgId}-visitorInvite:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasSigninWrite } = useValidateScopes([
    'o{orgId}-visitorSignIn:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasSignoutWrite } = useValidateScopes([
    'o{orgId}-visitorSignOut:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const locationCount = locations.length;

  // Check location license limit (includes user tier checking)
  const { totalLicenses } = useVisitorFeature(orgId);

  const licensesLeft = totalLicenses - locationCount;

  const [isSelectLocationModalOpen, setIsSelectLocationModalOpen] =
    useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<QuickAction | null>(null);
  const [visitorsToday, setVisitorsToday] = useState<VisitorSearch[]>([]);
  const [visitorNameFilter, setVisitorNameFilter] = useState<
    { firstName: string; middleName: string; lastName: string } | undefined
  >(undefined);
  const [newVisitorName, setNewVisitorName] = useState<
    | { firstName: string; middleName: string; lastName: string; dob?: string }
    | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [signInWorkflowConfig, setSignInWorkflowConfig] = useState<
    VisitorWorkflowConfig | undefined
  >(undefined);
  const [signInMethodLoading, setSignInMethodLoading] =
    useState<boolean>(false);

  const locationIdRef = useRef<number | null>(null);

  // sign in
  const [isSignInMethodModalOpen, setIsSignInMethodModalOpen] =
    useState<boolean>(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState<boolean>(false);
  const [isSignInManuallyDrawerOpen, setIsSignInManuallyDrawerOpen] =
    useState<boolean>(false);
  const [isScanIDModalOpen, setIsScanIDModalOpen] = useState<boolean>(false);
  const [isScanQRCodeModalOpen, setIsScanQRCodeModalOpen] =
    useState<boolean>(false);
  // sign out
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState<boolean>(false);
  // invite
  const [isNewInvitationDrawerOpen, setIsNewInvitationDrawerOpen] =
    useState<boolean>(false);
  // visitors drawer
  const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] =
    useState<boolean>(false);
  const { visitorId: _visitorId } = useUrlSearchParams();
  const removeQueryParams = useRemoveQueryParams();

  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (invitationConfig === null) {
          dispatch(fetchInvitationConfig({ orgId }));
        }
        if (orgId && locations.length === 0) {
          dispatch(fetchLocations({ orgId, status: 1 }));
        }
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, dispatch, invitationConfig, locations?.length]);

  const checkLicenseAndProceed = (
    actionType: QuickAction,
    action: () => void,
  ) => {
    const actionMessages = {
      NEW_INVITATION: 'schedule visitors',
      SIGN_IN: 'sign in visitors',
      SIGN_OUT: 'sign out visitors',
    };

    const actionMessage = actionMessages[actionType];

    if (locationCount > totalLicenses) {
      notificationService.show(
        'error',
        `Cannot ${actionMessage}. You have ${Math.max(0, licensesLeft)} licenses remaining.`,
      );
      return;
    }

    action();
  };

  const handleFloatButtonClick = async (type: QuickAction) => {
    if (type === 'SIGN_IN') {
      // Open the method modal immediately in a loading state so the user gets
      // instant feedback, then fetch the workflow config to populate it.
      setSignInWorkflowConfig(undefined);
      setSignInMethodLoading(true);
      setIsSignInMethodModalOpen(true);

      try {
        const locationWorkflowResponse = (await heliumReceive(
          'locationConfig',
          [orgId, locationIdRef.current],
        )) as LocationWorkflowResponse;
        const signInWorkflowConfigResponse = (await heliumReceive(
          'getVisitorWorkflowConfig',
          [orgId, locationWorkflowResponse?.data?.[0]?.SignInWorkflowId],
        )) as SignInWorkflowConfigResponse;
        const workflowConfig = signInWorkflowConfigResponse.data?.[0];
        setSignInWorkflowConfig(workflowConfig);

        // With only a single sign-in method (no ID scan / pre-registration),
        // skip the chooser and go straight to the visitor sign-in modal.
        if (
          !(
            (workflowConfig?.scan === 1 && workflowConfig?.scanScanner === 1) ||
            invitationConfig?.allowPreRegistration === 1
          )
        ) {
          setIsSignInMethodModalOpen(false);
          setIsSignInModalOpen(true);
        }
      } catch {
        setIsSignInMethodModalOpen(false);
        setIsSignInModalOpen(true);
      } finally {
        setSignInMethodLoading(false);
      }
    } else if (type === 'SIGN_OUT') {
      setIsSignOutModalOpen(true);
    } else if (type === 'NEW_INVITATION') {
      setIsNewInvitationDrawerOpen(true);
    }
  };

  const handleLocationSelect = async (locationId: number) => {
    locationIdRef.current = locationId;
    setIsSelectLocationModalOpen(false);

    // Kick off the pending action right away so the next modal opens without
    // waiting on the today's-visits fetch below.
    if (pendingAction) {
      handleFloatButtonClick(pendingAction);
      setPendingAction(null);
    }

    const selectedLocation = locations.find((loc) => loc.id === locationId);
    const tz = selectedLocation?.timezone || 'UTC';

    // Fetch visits for today for the selected location (used later by scan modals)
    try {
      const response = (await heliumReceive('getSearchVisitors', [orgId], {
        queryStringParams: {
          locationId: locationIdRef.current,
          startDate: dayjs()
            .tz(tz)
            .startOf('day')
            .utc()
            .format(DATE_TIME_FORMAT),
          endDate: dayjs().tz(tz).endOf('day').utc().format(DATE_TIME_FORMAT),
        },
      })) as SearchVisitorsResponse;
      setVisitorsToday(response.data ?? []);
    } catch {
      // Failed to fetch visits for today
    }
  };

  return (
    <>
      <OpModal
        title={t('quickActionsModal.title')}
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        loading={loading}
      >
        {locationCount > totalLicenses && (
          <div style={{ marginBottom: 16 }}>
            <RequestLicensesInlineMessage
              type="error"
              orgId={orgId}
              featureName="visitor"
              packageId={visitorSmallPackageId}
              message={`Cannot create visitors. You have ${Math.max(0, licensesLeft)} licenses remaining.`}
            />
          </div>
        )}
        <OpRow gutter={[16, 16]} justify="center">
          {hasInviteWrite && invitationConfig?.allowInvitations === 1 && (
            <OpCol xs={24} sm={24} md={8} lg={8} style={{ padding: '8px' }}>
              <QuickActionCard
                icon={
                  <MailOutlined
                    style={{
                      color: 'white',
                      backgroundColor: 'rgba(19,151,213,1.0)',
                      borderRadius: 20,
                      fontSize: 24,
                      padding: 8,
                    }}
                  />
                }
                title={t('quickActionsModal.scheduleVisitor')}
                color="rgba(19,151,213,0.1)"
                onClick={() =>
                  checkLicenseAndProceed('NEW_INVITATION', async () => {
                    try {
                      onClose();
                      if (locations?.length === 1) {
                        // Directly handle sign-in if only one location exists
                        await handleLocationSelect(locations[0].id);
                        setIsNewInvitationDrawerOpen(true);
                      } else {
                        // Open location modal for multiple locations
                        setPendingAction('NEW_INVITATION');
                        setIsSelectLocationModalOpen(true);
                      }
                    } catch {
                      // Error fetching locations
                    }
                  })
                }
                data-testid="quick-action-schedule-visitor"
              />
            </OpCol>
          )}
          {hasSigninWrite && (
            <OpCol xs={24} sm={24} md={8} lg={8} style={{ padding: '8px' }}>
              <QuickActionCard
                icon={
                  <LoginOutlined
                    style={{
                      color: 'white',
                      backgroundColor: 'rgba(109,152,138,1.0)',
                      borderRadius: 20,
                      fontSize: 24,
                      padding: 8,
                    }}
                  />
                }
                title={t('quickActionsModal.signInVisitor')}
                color="rgba(109,152,138,0.1)"
                onClick={() =>
                  checkLicenseAndProceed('SIGN_IN', async () => {
                    try {
                      onClose();
                      if (locations?.length === 1) {
                        // Directly handle sign-in if only one location exists
                        await handleLocationSelect(locations[0].id);
                        await handleFloatButtonClick('SIGN_IN');
                      } else {
                        // Open location modal for multiple locations
                        setPendingAction('SIGN_IN');
                        setIsSelectLocationModalOpen(true);
                      }
                    } catch {
                      // Error fetching locations
                    }
                  })
                }
              />
            </OpCol>
          )}
          {hasSignoutWrite && (
            <OpCol xs={24} sm={24} md={8} lg={8} style={{ padding: '8px' }}>
              <QuickActionCard
                icon={
                  <LogoutOutlined
                    style={{
                      color: 'white',
                      backgroundColor: 'rgba(155,154,154,1.0)',
                      borderRadius: 20,
                      fontSize: 24,
                      padding: 8,
                    }}
                  />
                }
                title={t('quickActionsModal.signOutVisitor')}
                color="rgba(155,154,154,0.1)"
                onClick={() =>
                  checkLicenseAndProceed('SIGN_OUT', async () => {
                    try {
                      onClose();
                      if (locations?.length === 1) {
                        // Directly handle sign-in if only one location exists
                        await handleLocationSelect(locations[0].id);
                        setIsSignOutModalOpen(true);
                      } else {
                        // Open location modal for multiple locations
                        setPendingAction('SIGN_OUT');
                        setIsSelectLocationModalOpen(true);
                      }
                    } catch {
                      // Error fetching locations
                    }
                  })
                }
              />
            </OpCol>
          )}
        </OpRow>
      </OpModal>

      {isSelectLocationModalOpen && (
        <SelectLocationModal
          open={isSelectLocationModalOpen}
          onClose={() => setIsSelectLocationModalOpen(false)}
          handleLocationSelect={handleLocationSelect}
        />
      )}
      {isSignInMethodModalOpen && locationIdRef.current !== null && (
        <SignInMethodModal
          open={isSignInMethodModalOpen}
          onClose={() => setIsSignInMethodModalOpen(false)}
          openSignInModal={() => setIsSignInModalOpen(true)}
          openScanIDModal={() => setIsScanIDModalOpen(true)}
          openScanQRCodeModal={() => setIsScanQRCodeModalOpen(true)}
          openSignInManuallyDrawer={() => setIsSignInManuallyDrawerOpen(true)}
          locationIdRef={locationIdRef.current}
          loading={signInMethodLoading}
          signInWorkflowConfig={signInWorkflowConfig}
        />
      )}
      {isSignInModalOpen && locationIdRef.current !== null && (
        <SignInModal
          open={isSignInModalOpen}
          locationIdRef={locationIdRef.current}
          onClose={() => {
            setIsSignInModalOpen(false);
            setVisitorNameFilter(undefined);
          }}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
          openSignInManuallyDrawer={() => setIsSignInManuallyDrawerOpen(true)}
          visitorNameFilter={visitorNameFilter}
        />
      )}
      {isSignInManuallyDrawerOpen && locationIdRef.current !== null && (
        <SignInManuallyDrawer
          open={isSignInManuallyDrawerOpen}
          locationIdRef={locationIdRef.current}
          onClose={() => {
            setNewVisitorName(undefined);
            setVisitorNameFilter(undefined);
            setIsSignInManuallyDrawerOpen(false);
          }}
          newVisitorName={newVisitorName}
          setNewVisitorName={setNewVisitorName}
        />
      )}
      {isScanIDModalOpen && (
        <ScanIDModal
          open={isScanIDModalOpen}
          visitorsToday={visitorsToday}
          onClose={() => setIsScanIDModalOpen(false)}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
          openSelectVisitorsModal={() => setIsSignInModalOpen(true)}
          openSignInManuallyDrawer={() => setIsSignInManuallyDrawerOpen(true)}
          setVisitorNameFilter={setVisitorNameFilter}
          setNewVisitorName={setNewVisitorName}
        />
      )}
      {isScanQRCodeModalOpen && (
        <ScanQRCodeModal
          open={isScanQRCodeModalOpen}
          visitorsToday={visitorsToday}
          onClose={() => setIsScanQRCodeModalOpen(false)}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
        />
      )}
      {isSignOutModalOpen && locationIdRef.current !== null && (
        <SignOutModal
          open={isSignOutModalOpen}
          locationIdRef={locationIdRef.current}
          onClose={() => setIsSignOutModalOpen(false)}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
        />
      )}
      {isNewInvitationDrawerOpen && locationIdRef.current !== null && (
        <InvitationDrawer
          open={isNewInvitationDrawerOpen}
          onClose={() => {
            setIsNewInvitationDrawerOpen(false);
          }}
          locationIdRef={locationIdRef.current}
        />
      )}
      {isVisitorsDrawerOpen && (
        <VisitorsDrawer
          open={isVisitorsDrawerOpen}
          onClose={() => {
            setNewVisitorName(undefined);
            setVisitorNameFilter(undefined);
            setIsVisitorsDrawerOpen(false);
            dispatch(setSelectedVisit(null));
            removeQueryParams(['drawer', 'visitId', 'visitorId']);
          }}
        />
      )}
    </>
  );
};

export default QuickActionsModal;
